import {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from "@tiptap/suggestion";
import { PureEditorContent, ReactRenderer } from "@tiptap/react";
import MentionList from "@src/features/libraryItem/course/build/comments/mentions/MentionList";
import tippy, { Instance, Props } from "tippy.js";
import gql from "graphql-tag";
import {
  AtMentionEmployeeFragment,
  CommentMentionPeopleDocument,
} from "@src/features/libraryItem/course/build/comments/mentions/hooks/useMentionConfig.generated";
import { useApolloClient } from "@apollo/client";
import { Ref, useMemo } from "react";

const useMentionConfig = (
  ref: Ref<PureEditorContent> | undefined | null,
): Omit<SuggestionOptions<AtMentionEmployeeFragment>, "editor"> => {
  const client = useApolloClient();
  return useMemo(
    () => ({
      items: async ({ query }) => {
        const { data } = await client.query({
          query: CommentMentionPeopleDocument,
        });
        const people: AtMentionEmployeeFragment[] =
          data?.AdminEmployees.objects || [];
        return people
          .filter((item) =>
            item.name?.toLowerCase().startsWith(query.toLowerCase()),
          )
          .slice(0, 5);
      },

      render: () => {
        let component: ReactRenderer | undefined;
        let popup: Instance<Props>[] | undefined;

        return {
          onStart: (props: SuggestionProps) => {
            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            });
            if (!ref) {
              return;
            }
            // @ts-ignore
            const current = ref.current?.editorContentRef?.current;
            if (current) {
              popup = tippy(current, {
                // clientRect can return null mid-edit (tiptap re-queries the
                // decoration node); popper crashes reading .width off null.
                getReferenceClientRect: () =>
                  props.clientRect?.() ?? new DOMRect(),
                appendTo: () => current,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
              });
            }
          },

          onUpdate(props: SuggestionProps) {
            if (component) {
              component.updateProps(props);
            }
            if (popup && popup.length > 0) {
              popup[0].setProps({
                getReferenceClientRect: () =>
                  props.clientRect?.() ?? new DOMRect(),
              });
            }
          },

          onKeyDown(props: SuggestionKeyDownProps) {
            if (props.event.key === "Escape") {
              if (popup) {
                popup[0].hide();
              }

              return true;
            }
            if (component && component.ref) {
              // @ts-ignore
              return component.ref?.onKeyDown(props);
            }
          },

          onExit() {
            if (popup && popup.length > 0) {
              popup[0].destroy();
            }
            if (component) {
              component.destroy();
            }
          },
        };
      },
    }),
    [client, ref],
  );
};

export default useMentionConfig;

gql`
  query CommentMentionPeople {
    AdminEmployees(userTypes: [ADMIN, MANAGER]) {
      objects {
        ...AtMentionEmployee
      }
    }
  }

  fragment AtMentionEmployee on Employee {
    id
    name
    profileImage {
      ...AvatarProfileImage
    }
  }
`;
